import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimeCard from "@/components/feature/AnimeCard";
import usePageMeta from "@/hooks/usePageMeta";
import { animeCategories, animeList } from "@/mocks/anime";

export default function Category() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const activeCat = animeCategories.find((c) => c.id === id);

  usePageMeta(
    activeCat
      ? `${activeCat.name}推荐 · 小水看番泡面番推荐榜单`
      : "分类榜单 · 小水看番泡面番推荐/三分钟短番",
    activeCat
      ? `${activeCat.name}：${activeCat.tagline}。小水看番精选${activeCat.name}短篇，短篇速刷不迷路，一口气看完清单。`
      : "小水看番全部分类榜单：下饭番、摸鱼短番、搞笑短篇、治愈系、泡面三分钟、一口气看完，按口味分好类。",
  );

  const filtered = useMemo(() => {
    let list = animeList;
    if (id && activeCat) {
      list = list.filter((a) =>
        [...a.genres, ...a.tags, a.title, a.synopsis].some((t) =>
          activeCat.match.some((m) => t.toLowerCase().includes(m.toLowerCase())),
        ),
      );
    }
    if (q.trim()) {
      const kw = q.trim().toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(kw) ||
          a.titleEn.toLowerCase().includes(kw) ||
          a.genres.some((g) => g.toLowerCase().includes(kw)) ||
          a.tags.some((t) => t.toLowerCase().includes(kw)) ||
          a.synopsis.toLowerCase().includes(kw),
      );
    }
    return [...list].sort((a, b) => b.rating - a.rating);
  }, [id, q, activeCat]);

  function selectCat(catId: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (catId === "all") {
        next.delete("q");
      }
      return next;
    });
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="bg-gradient-to-br from-primary-50 via-background-50 to-accent-50 pt-24 pb-10">
          <div className="container-x">
            <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
              <Link to="/" className="hover:text-primary-600">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/biaoqian" className="hover:text-primary-600">分类榜单</Link>
              {activeCat && (
                <>
                  <i className="ri-arrow-right-s-line"></i>
                  <span className="text-foreground-700">{activeCat.name}</span>
                </>
              )}
            </nav>

            <div className="mt-5 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
              <div>
                <h1 className="font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                  {activeCat ? `${activeCat.name}推荐` : "全部短番榜单"}
                </h1>
                <p className="mt-2 max-w-xl text-sm text-foreground-600">
                  {activeCat
                    ? `${activeCat.tagline} · ${activeCat.desc}`
                    : "泡面番、三分钟短番、日常搞笑短篇全收录，按口味分好类，短篇速刷不迷路。"}
                </p>
              </div>
              <p className="text-sm text-foreground-500">
                共 <span className="font-bold text-primary-600">{filtered.length}</span> 部短番
              </p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setSearchParams({})}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  !id && !q
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 border border-background-200 text-foreground-600 hover:bg-background-200"
                }`}
              >
                全部
              </button>
              {animeCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/biaoqian/${cat.id}`}
                  onClick={() => selectCat(cat.id)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    id === cat.id
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-50 border border-background-200 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {q && (
              <p className="mt-4 text-sm text-foreground-600">
                搜索「<span className="font-bold text-primary-600">{q}</span>」的结果：
              </p>
            )}
          </div>
        </section>

        <section className="container-x py-10 md:py-14">
          {filtered.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((anime) => (
                <AnimeCard key={anime.id} anime={anime} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-20 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-background-100 text-3xl text-foreground-300">
                <i className="ri-search-eye-line"></i>
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
                没找到相关的短番
              </h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-600">
                换个关键词或换个分类试试吧，小水正在努力扩充更多泡面番和摸鱼短番。
              </p>
              <Link
                to="/biaoqian"
                className="btn-pill mt-6 bg-primary-500 text-background-50 hover:bg-primary-600"
              >
                返回全部榜单
              </Link>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}